import React from "react";
import PropTypes from "prop-types";

import { UINumberField, UISelect, UITextField } from "../../../../../ui";
import { getVisibleError, toNumber } from "../helpers";
import {
  fillTemplate,
  formatAmount,
  getExceededBy,
  getRemainingForField,
  getTotalApplicants,
} from "../../ChooseCrewSize/crewSizeUtils";
import { useSelector } from "react-redux";
import messages from "../../../../../../assets/locale/messages";

const RoleFields = ({
  formik,
  role,
  roleIndex,
  options,
  capacityCeiling,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;

  /** Inline "Only X spots left" when this entry takes the event above the crew size ceiling. */
  const getCapacityFieldProps = (field) => {
    const roles = formik.values.event_roles_attributes;
    if (
      capacityCeiling == null ||
      getTotalApplicants(roles) <= capacityCeiling ||
      !toNumber(role?.[field])
    ) {
      return {};
    }
    const remaining = getRemainingForField(
      roles,
      roleIndex,
      field,
      capacityCeiling,
    );
    // No spots left at all: say how many to remove instead of "Only 0 spots left".
    // Total − ceiling, from the roles and the plan the event already returns (PROLINE-4015 adds no field).
    return {
      error: true,
      helperText:
        remaining > 0
          ? fillTemplate(t.spotsLeft, { count: formatAmount(remaining) })
          : fillTemplate(t.removeToFit, {
              count: formatAmount(getExceededBy(roles, capacityCeiling)),
            }),
    };
  };
  const selectedRoleIds = formik.values.event_roles_attributes
    .map((item, index) => (index === roleIndex ? null : item.role_id))
    .filter(
      (id) => id !== null && id !== undefined && id !== "",
    )
    .map(String);
  const availableOptions = options.filter(
    (option) => !selectedRoleIds.includes(String(option.id)),
  );

  return(
  <div className="roles-field-grid roles-field-grid--four">
   
    <UISelect
      label={t.roleName}
      name={`event_roles_attributes.${roleIndex}.role_id`}
      placeholder={t.roleNamePlaceholder}
      required
      options={availableOptions}
      isOptionEqualToValue={(option, selected) =>
        option != null &&
        selected != null &&
        String(option.id) === String(selected.id)
      }
      value={
        options.find((option) => String(option.id) === String(role.role_id)) ||
        null
      }
      onChange={(option) => {
        const id =
          option?.id === null || option?.id === undefined ? "" : option.id;
        const nameStr = option?.name ?? "";
        const roles = [...formik.values.event_roles_attributes];
        roles[roleIndex] = {
          ...roles[roleIndex],
          role_id: id,
          role_name: nameStr,
        };
        formik.setFieldValue("event_roles_attributes", roles, true);
        formik.setFieldTouched(
          `event_roles_attributes.${roleIndex}.role_id`,
          true,
          false,
        );
      }}
      helperText={getVisibleError(
        formik,
        `event_roles_attributes.${roleIndex}.role_id`,
      )}
    />
    <UINumberField
      label={t.numberOfMales}
      name={`event_roles_attributes.${roleIndex}.maximum_number_of_males`}
      placeholder={t.numberOfMalesPlaceholder}
      required
      min={0}
      step={1}
      helperText={getVisibleError(
        formik,
        `event_roles_attributes.${roleIndex}.maximum_number_of_males`,
      )}
      {...getCapacityFieldProps("maximum_number_of_males")}
    />
    <UINumberField
      label={t.numberOfFemales}
      name={`event_roles_attributes.${roleIndex}.maximum_number_of_females`}
      placeholder={t.numberOfFemalesPlaceholder}
      required
      min={0}
      step={1}
      helperText={getVisibleError(
        formik,
        `event_roles_attributes.${roleIndex}.maximum_number_of_females`,
      )}
      {...getCapacityFieldProps("maximum_number_of_females")}
    />
    <UITextField
      label={t.uniformCode}
      name={`event_roles_attributes.${roleIndex}.uniform_code`}
      placeholder={t.uniformCodePlaceholder}
      helperText={getVisibleError(
        formik,
        `event_roles_attributes.${roleIndex}.uniform_code`,
      )}
    />
  </div>
)};

RoleFields.propTypes = {
  formik: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  roleIndex: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  /** Crew size ceiling (company create only); null disables the capacity check. */
  capacityCeiling: PropTypes.number,
};

RoleFields.defaultProps = {
  options: [],
  capacityCeiling: null,
};

export default RoleFields;
